import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getActiveDomain,
  setActiveDomain,
  readLocalActiveDomain,
  writeLocalActiveDomain,
} from '@/data/copilot';
import { ApiError } from '@/lib/api';
import {
  adaptSnapshot,
  reconcileActiveDomain,
  type ActiveDomain,
  type ActiveDomainSnapshot,
  type DomainOption,
} from '@/data/active-domain';

/* ----------------------------------------------------------------------------
 * useActiveDomain — the shell's single source of truth for the active SEO
 * domain (Phase 2, Lane 2A). The SERVER is authoritative (GET/POST
 * /v1/ai/active-domain, via the Phase-1 copilot client); localStorage is only
 * an instant-paint restore hint, always reconciled against the server value.
 *
 *  - exactly one connected domain → auto-selected silently (no prompt/chips):
 *    single-domain workspaces stay frictionless, behaviour-equivalent to today.
 *  - many domains, a server choice recorded → that choice wins (survives refresh
 *    on any device); a differing browser hint is ignored.
 *  - many domains, no server choice, a valid browser hint → restored AND
 *    persisted so it becomes canonical; otherwise the caller shows the chooser.
 *  - "All domains" is an explicit, session-level workspace-wide lens (active =
 *    null, no prompt). It is NOT persisted server-side — the Phase-1 store has
 *    no "clear" verb — so it is honestly a per-session lens (see note in return).
 *
 * The client never invents a domain; the server re-validates every project_id
 * for tenancy (foreign id → 422, surfaced as an error, never a silent fallback).
 * -------------------------------------------------------------------------- */

const QUERY_KEY = ['active-domain'] as const;
const EMPTY: ActiveDomainSnapshot = { active: null, domains: [] };

/** Module-level guard so N mounted hook instances don't each re-POST the same
 *  silent auto-selection. POST is idempotent; this just avoids redundant calls. */
const autoSelectInFlight = new Set<number>();

export interface UseActiveDomainResult {
  /** the resolved active domain, or null (none chosen / "All domains" lens). */
  active: ActiveDomain | null;
  /** every connected domain for the active workspace (server list). */
  domains: DomainOption[];
  /** true when the workspace has exactly one connected domain. */
  isSingleDomain: boolean;
  /** true when the explicit workspace-wide ("All domains") lens is selected. */
  isAllDomains: boolean;
  /** true when multi-domain and nothing is resolved yet (show the chooser). */
  needsChoice: boolean;
  /** pin a domain: POST (server-validated), persist the hint, refresh. */
  select: (projectId: number) => Promise<void>;
  /** switch to the workspace-wide lens (active = null; not server-persisted). */
  selectAll: () => void;
  isLoading: boolean;
  /** last select() error (e.g. a 422 foreign-domain rejection), else null. */
  error: string | null;
}

export function useActiveDomain(): UseActiveDomainResult {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Explicit, session-only "All domains" lens (cleared the moment a domain is
  // pinned). Not persisted: the Phase-1 store cannot represent "no active".
  const [viewAll, setViewAll] = useState(false);

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => adaptSnapshot(await getActiveDomain()),
    staleTime: 60_000,
    retry: false,
  });

  const snapshot = query.data ?? EMPTY;
  const hintRef = useRef<number | null>(readLocalActiveDomain());
  const { active, needsChoice, autoSelectId } = reconcileActiveDomain(snapshot, hintRef.current);

  // Persist a silent auto-selection / hint-restore so the server becomes
  // canonical and the choice survives the next refresh. Idempotent + guarded.
  useEffect(() => {
    if (autoSelectId == null || viewAll) return;
    if (autoSelectInFlight.has(autoSelectId)) return;
    autoSelectInFlight.add(autoSelectId);
    let cancelled = false;
    void (async () => {
      try {
        const res = await setActiveDomain(autoSelectId);
        if (cancelled) return;
        writeLocalActiveDomain(autoSelectId);
        if (res.active) {
          qc.setQueryData<ActiveDomainSnapshot>(QUERY_KEY, (prev) => ({
            active: adaptSnapshot({ active: res.active, domains: [] }).active,
            domains: (prev ?? snapshot).domains,
          }));
        }
      } catch {
        /* non-fatal: reconcile still shows the optimistic active; server stays
           source of truth on the next fetch. */
      } finally {
        autoSelectInFlight.delete(autoSelectId);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectId, viewAll]);

  const select = useCallback(
    async (projectId: number) => {
      setError(null);
      setViewAll(false);
      try {
        const res = await setActiveDomain(projectId);
        writeLocalActiveDomain(projectId);
        hintRef.current = projectId;
        qc.setQueryData<ActiveDomainSnapshot>(QUERY_KEY, (prev) => ({
          active: res.active ? adaptSnapshot({ active: res.active, domains: [] }).active : (prev ?? snapshot).active,
          domains: (prev ?? snapshot).domains,
        }));
        await qc.invalidateQueries({ queryKey: QUERY_KEY });
      } catch (e) {
        setError(
          e instanceof ApiError && e.status === 422
            ? 'That domain is not available in this workspace.'
            : 'Could not switch domain just now. Nothing changed.',
        );
        throw e;
      }
    },
    [qc, snapshot],
  );

  const selectAll = useCallback(() => {
    setError(null);
    setViewAll(true);
    // Drop the restore hint so a stale domain does not re-pin on refresh.
    writeLocalActiveDomain(null);
    hintRef.current = null;
  }, []);

  return {
    active: viewAll ? null : active,
    domains: snapshot.domains,
    isSingleDomain: snapshot.domains.length === 1,
    isAllDomains: viewAll,
    needsChoice: viewAll ? false : needsChoice,
    select,
    selectAll,
    isLoading: query.isLoading,
    error,
  };
}
